/**
 * VWAP Absorption strategy for the dashboard backtest workspace.
 *
 * Fades 2-sigma session-VWAP extensions that show: (1) an armed extension,
 * (2) a volume-profile HVN/LVN node behind the extreme, (3) a CVD divergence
 * against a prior swing, and (4) 1m/5m/30m closes confirming back inside the
 * band. Requires 1-minute bars.
 */

import { atrFromBars } from '../indicators/atr';
import { buildSessionIndex } from '../indicators/rthSession';
import { sessionVwapFromBars } from '../indicators/sessionVwap';
import { barDeltaFromBars } from '../indicators/barDelta';
import { computeVolumeProfile } from '../../services/volumeProfile/volumeProfileCalculator';

function barMinutesSpacing(bars) {
  for (let i = 1; i < Math.min(bars.length, 50); i++) {
    const a = Number(bars[i - 1]?.timestamp ?? bars[i - 1]?.timeSec * 1000);
    const b = Number(bars[i]?.timestamp ?? bars[i]?.timeSec * 1000);
    if (Number.isFinite(a) && Number.isFinite(b) && b > a) return (b - a) / 60000;
  }
  return null;
}

function parseConfirmTfs(value) {
  const str = String(value ?? '1,5,30');
  const set = new Set(str.split(',').map((s) => Number(s.trim())).filter((n) => [1, 5, 30].includes(n)));
  if (set.size === 0) set.add(1);
  return set;
}

/**
 * Filter an engine-supplied aligned timeframe (`{ bars, idxAt, completedAt, at }`,
 * see engine/timeframes.js) down to RTH-only HTF bars, using the existing
 * `buildSessionIndex` helper so results stay consistent with the
 * session-anchored VWAP logic elsewhere in this file. Native 5m/30m bars
 * fetched upstream may include non-RTH bars (overnight/extended session),
 * whereas the prior internal aggregation only ever produced RTH buckets.
 *
 * Returns per-base-bar `close`/`completedAt` arrays shaped like the old
 * `aggregateHigherTf` output (`tf5Close`/`tf5Idx`-style), but sourced from
 * the engine's aligned series instead of hand-rolled resampling.
 */
function rthHtfSeries(tfEntry, baseLen) {
  const close = new Array(baseLen).fill(null);
  const completedIdx = new Array(baseLen).fill(null);
  if (!tfEntry || !Array.isArray(tfEntry.bars) || !tfEntry.bars.length) {
    return { close, completedIdx };
  }

  const { bars: htfBars, idxAt, completedAt } = tfEntry;
  const htfSessionInfo = buildSessionIndex(htfBars);
  const isHtfRth = (idx) => Boolean(htfSessionInfo[idx]?.isRth);

  for (let i = 0; i < baseLen; i++) {
    const htfIdx = idxAt[i];
    if (htfIdx == null || htfIdx < 0 || !htfBars[htfIdx] || !isHtfRth(htfIdx)) continue;
    close[i] = htfBars[htfIdx].close;
    completedIdx[i] = completedAt[htfIdx];
  }
  return { close, completedIdx };
}

function smoothRows(rows, width = 1) {
  return rows.map((r, idx) => {
    const lo = Math.max(0, idx - width);
    const hi = Math.min(rows.length - 1, idx + width);
    let sum = 0;
    let count = 0;
    for (let j = lo; j <= hi; j++) { sum += rows[j].total; count++; }
    return count > 0 ? sum / count : 0;
  });
}

function findNodes(rows, { nodeWidth = 3, hvnPct = 1.3, lvnPct = 0.5 } = {}) {
  if (!rows.length) return { hvns: [], lvns: [] };
  const smoothed = smoothRows(rows, 1);
  const mean = smoothed.reduce((s, v) => s + v, 0) / smoothed.length || 0;
  const hvns = [];
  const lvns = [];
  for (let i = 0; i < rows.length; i++) {
    const lo = Math.max(0, i - nodeWidth);
    const hi = Math.min(rows.length - 1, i + nodeWidth);
    let isMax = true;
    let isMin = true;
    for (let j = lo; j <= hi; j++) {
      if (j === i) continue;
      if (smoothed[j] > smoothed[i]) isMax = false;
      if (smoothed[j] < smoothed[i]) isMin = false;
    }
    if (isMax && smoothed[i] >= hvnPct * mean) hvns.push({ price: rows[i].price, value: smoothed[i] });
    if (isMin && smoothed[i] <= lvnPct * mean) lvns.push({ price: rows[i].price, value: smoothed[i], idx: i });
  }
  // LVNs only count when sitting between two HVNs
  const hvnIdxSet = new Set(hvns.map((h) => rows.findIndex((r) => r.price === h.price)));
  const hvnIdxs = [...hvnIdxSet].sort((a, b) => a - b);
  const validLvns = lvns.filter((lvn) => {
    const below = hvnIdxs.filter((idx) => idx < lvn.idx).pop();
    const above = hvnIdxs.find((idx) => idx > lvn.idx);
    return below != null && above != null;
  });
  return { hvns, lvns: validLvns };
}

export const vwapAbsorption = {
  id: 'vwap-absorption',
  name: 'VWAP Absorption',
  indicators: ['Session VWAP', 'Volume Profile', 'CVD', 'ATR'],
  baseTimeframe: { interval: 1, unit: 'Minute' },
  requiredTimeframes: [
    { key: '5m', interval: 5, unit: 'Minute' },
    { key: '30m', interval: 30, unit: 'Minute' },
  ],

  chartOverlays(params = {}) {
    return [
      { key: 'vwap', label: 'Session VWAP', colorKey: 'primary', lineWidth: 1 },
      { key: 'up1', label: `VWAP +${params.innerMult ?? 1}σ`, colorKey: 'secondary', lineWidth: 1 },
      { key: 'dn1', label: `VWAP -${params.innerMult ?? 1}σ`, colorKey: 'secondary', lineWidth: 1 },
      { key: 'up2', label: `VWAP +${params.bandMult ?? 2}σ`, colorKey: 'danger', lineWidth: 1 },
      { key: 'dn2', label: `VWAP -${params.bandMult ?? 2}σ`, colorKey: 'danger', lineWidth: 1 },
    ];
  },

  paramSchema: [
    { key: 'bandMult', label: 'Outer band σ', type: 'number', default: 2.0, min: 0.5, max: 5, step: 0.1, section: 'VWAP' },
    { key: 'innerMult', label: 'Inner band σ', type: 'number', default: 1.0, min: 0.1, max: 5, step: 0.1, section: 'VWAP' },
    { key: 'armBars', label: 'Arm window (bars)', type: 'number', default: 30, min: 1, max: 200, step: 1, section: 'VWAP' },

    { key: 'vpSessions', label: 'VP sessions', type: 'number', default: 5, min: 1, max: 20, step: 1, section: 'Volume Profile' },
    { key: 'tickSize', label: 'Tick size', type: 'number', default: 0.25, min: 0.01, max: 10, step: 0.01, section: 'Volume Profile' },
    { key: 'vpRows', label: 'VP rows', type: 'number', default: 80, min: 10, max: 400, step: 1, section: 'Volume Profile' },
    { key: 'nodeWidth', label: 'Node width (rows)', type: 'number', default: 3, min: 1, max: 20, step: 1, section: 'Volume Profile' },
    { key: 'hvnPct', label: 'HVN % of mean', type: 'number', default: 1.3, min: 1, max: 5, step: 0.05, section: 'Volume Profile' },
    { key: 'lvnPct', label: 'LVN % of mean', type: 'number', default: 0.5, min: 0.05, max: 1, step: 0.05, section: 'Volume Profile' },
    { key: 'levelTolAtr', label: 'Level tol × ATR', type: 'number', default: 0.25, min: 0.01, max: 3, step: 0.01, section: 'Volume Profile' },
    { key: 'atrPeriod', label: 'ATR period', type: 'number', default: 14, min: 2, max: 200, step: 1, section: 'Volume Profile' },

    { key: 'divLookback', label: 'Divergence lookback', type: 'number', default: 60, min: 5, max: 400, step: 1, section: 'Divergence' },
    { key: 'divMinGap', label: 'Divergence min gap', type: 'number', default: 5, min: 1, max: 100, step: 1, section: 'Divergence' },
    { key: 'divMinDelta', label: 'Divergence min ΔCVD', type: 'number', default: 0, min: 0, max: 100000, step: 1, section: 'Divergence' },

    { key: 'confirmTfs', label: 'Confirm TFs', type: 'text', default: '1,5,30', section: 'Confirmation' },

    { key: 'stopTicks', label: 'Stop ticks beyond extreme', type: 'number', default: 2, min: 0, max: 50, step: 1, section: 'Risk' },
    { key: 'tp1Pct', label: 'TP1 qty %', type: 'number', default: 0.5, min: 0.05, max: 0.95, step: 0.05, section: 'Risk' },
    { key: 'minRewardR', label: 'Min reward (R)', type: 'number', default: 0.8, min: 0, max: 10, step: 0.05, section: 'Risk' },

    { key: 'skipWednesday', label: 'Skip Wednesday (1/0)', type: 'number', default: 1, min: 0, max: 1, step: 1, section: 'Filters' },
    { key: 'thinVolPct', label: 'Thin session % of median', type: 'number', default: 0.5, min: 0.05, max: 1, step: 0.05, section: 'Filters' },
    { key: 'noTradeFirstMin', label: 'No-trade first N min', type: 'number', default: 60, min: 0, max: 390, step: 1, section: 'Filters' },
    { key: 'lastEntryMin', label: 'Last entry minute', type: 'number', default: 375, min: 0, max: 390, step: 1, section: 'Filters' },
  ],

  prepare(bars, params, tf = {}) {
    // Fallback guard: kept in case an older caller doesn't wire the `timeframes`
    // engine option through, so this still fails closed on bad bar spacing
    // rather than silently misbehaving.
    const spacing = barMinutesSpacing(bars || []);
    if (!bars?.length || !Number.isFinite(spacing) || Math.abs(spacing - 1) > 0.01) {
      return { ind: {}, state: { invalid: true } };
    }

    const sessionInfo = buildSessionIndex(bars);
    const { vwap, up1, dn1, up2, dn2, armed } = sessionVwapFromBars(bars, sessionInfo, {
      bandMult: params.bandMult,
      innerMult: params.innerMult,
      armBars: params.armBars,
    });
    const { cvd } = barDeltaFromBars(bars, sessionInfo);
    const atr = atrFromBars(bars, params.atrPeriod);

    const tf5Series = rthHtfSeries(tf['5m'], bars.length);
    const tf30Series = rthHtfSeries(tf['30m'], bars.length);

    // Thin-session flags, computed lazily inline below (needs volume + session order).
    const thinBySession = new Map();
    {
      const checkpointBySession = new Map();
      const sessionOrder = [];
      const cumBySession = new Map();
      for (let i = 0; i < bars.length; i++) {
        const info = sessionInfo[i];
        if (!info?.isRth) continue;
        if (!cumBySession.has(info.sessionIdx)) { cumBySession.set(info.sessionIdx, 0); sessionOrder.push(info.sessionIdx); }
        const cum = cumBySession.get(info.sessionIdx) + (Number(bars[i].volume) || 0);
        cumBySession.set(info.sessionIdx, cum);
        if (info.minuteOfSession === 60 && !checkpointBySession.has(info.sessionIdx)) {
          checkpointBySession.set(info.sessionIdx, cum);
          const priorIdxs = sessionOrder.filter((s) => s < info.sessionIdx).slice(-10);
          const priorVols = priorIdxs.map((s) => checkpointBySession.get(s)).filter(Number.isFinite);
          if (priorVols.length >= 3) {
            const sorted = [...priorVols].sort((a, b) => a - b);
            const mid = Math.floor(sorted.length / 2);
            const median = sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
            thinBySession.set(info.sessionIdx, median > 0 && cum < (params.thinVolPct ?? 0.5) * median);
          } else {
            thinBySession.set(info.sessionIdx, false);
          }
        }
      }
    }

    // Volume-profile node cache, recomputed every 30 minutes (per session bucket).
    const vpCache = new Map();
    const getNodesFor = (i) => {
      const info = sessionInfo[i];
      if (!info?.isRth) return { hvns: [], lvns: [] };
      const bucket = Math.floor(info.minuteOfSession / 30);
      const key = `${info.sessionIdx}:${bucket}`;
      if (vpCache.has(key)) return vpCache.get(key);
      const windowStart = Math.max(0, info.sessionIdx - (params.vpSessions - 1));
      const lo = bars.findIndex((_, j) => sessionInfo[j]?.isRth && sessionInfo[j].sessionIdx >= windowStart);
      const vpBars = lo >= 0 ? bars.slice(lo, i + 1) : bars.slice(0, i + 1);
      const profile = computeVolumeProfile(vpBars, { tickSize: params.tickSize, rowCount: params.vpRows });
      const nodes = findNodes(profile.rows, {
        nodeWidth: params.nodeWidth,
        hvnPct: params.hvnPct,
        lvnPct: params.lvnPct,
      });
      vpCache.set(key, nodes);
      return nodes;
    };

    return {
      ind: {
        vwap, up1, dn1, up2, dn2, armed, cvd, atr,
        tf5Close: tf5Series.close, tf5Idx: tf5Series.completedIdx,
        tf30Close: tf30Series.close, tf30Idx: tf30Series.completedIdx,
      },
      session: sessionInfo,
      state: {
        sessionInfo,
        thinBySession,
        getNodesFor,
        setups: {}, // keyed by extIdx -> { triggered }
      },
    };
  },

  explain(ctx) {
    const { i, ind, params, session, state } = ctx;
    if (state?.invalid) {
      return [{ id: 'interval', label: '1m bars required', pass: false }];
    }
    const info = session;
    const armedState = ind.armedNow ?? ind.armed?.[i];
    const sessionOk = Boolean(info?.isRth)
      && !(params.skipWednesday && info.isWednesday)
      && !info.isHalfDay
      && !(state?.thinBySession?.get(info.sessionIdx))
      && info.minuteOfSession >= params.noTradeFirstMin
      && info.minuteOfSession <= params.lastEntryMin;

    const checks = [
      { id: 'interval', label: '1m bars', pass: true },
      { id: 'session', label: 'Session OK', pass: sessionOk },
      { id: 'armed', label: 'Extension armed (2σ)', pass: Boolean(armedState) },
    ];
    if (!armedState) return checks;

    const vpNodes = state.getNodesFor(i);
    const atr = ind.atrNow ?? ind.atr?.[i];
    const tol = Math.max((params.levelTolAtr || 0) * (atr || 0), 2 * params.tickSize);
    const nodeHit = [...vpNodes.hvns, ...vpNodes.lvns].some((n) => Math.abs(n.price - armedState.extPrice) <= tol);
    checks.push({ id: 'node', label: 'Node behind extension', pass: nodeHit });

    const divergence = Boolean(findDivergence(ctx.bars, ind.cvd, armedState, params));
    checks.push({ id: 'divergence', label: 'CVD divergence', pass: divergence });

    const confirmed = Boolean(checkMultiTfConfirm(ctx.bars, ind, i, armedState, params));
    checks.push({ id: 'confirm', label: 'Multi-TF close inside band', pass: confirmed });

    const vwapNow = ind.vwapNow ?? ind.vwap?.[i];
    const close = ctx.bar?.close;
    const stopDist = Math.abs(close - armedState.extPrice);
    const rewardOk = Number.isFinite(vwapNow) && Number.isFinite(close) && stopDist > 0
      && Math.abs(vwapNow - close) >= (params.minRewardR || 0) * stopDist;
    checks.push({ id: 'reward', label: 'Reward ≥ min R', pass: rewardOk });

    return checks;
  },

  onBar(ctx) {
    const { i, bar, bars, ind, params, position, session, state } = ctx;
    if (state?.invalid) return [];
    const info = session;

    if (position) {
      if (info?.isRth && info.isLastOfSession) {
        return [{ type: 'exit', reason: 'session-end' }];
      }
      return [];
    }
    if (!info?.isRth) return [];
    if (params.skipWednesday && info.isWednesday) return [];
    if (info.isHalfDay) return [];
    if (state?.thinBySession?.get(info.sessionIdx)) return [];
    if (info.minuteOfSession < params.noTradeFirstMin || info.minuteOfSession > params.lastEntryMin) return [];

    // Close any open position at session end (handled via exit intent on the position itself,
    // but with no open position there's nothing to close here).
    const armedState = ind.armed?.[i];
    if (!armedState) return [];

    const setupKey = `${armedState.side}:${armedState.extIdx}`;
    if (state.setups[setupKey]) return [];

    const vpNodes = state.getNodesFor(i);
    const atr = ind.atr?.[i];
    const tol = Math.max((params.levelTolAtr || 0) * (atr || 0), 2 * params.tickSize);
    const nodeHit = [...vpNodes.hvns, ...vpNodes.lvns].some((n) => Math.abs(n.price - armedState.extPrice) <= tol);
    if (!nodeHit) return [];

    if (!findDivergence(bars, ind.cvd, armedState, params)) return [];
    if (!checkMultiTfConfirm(bars, ind, i, armedState, params)) return [];

    const vwapNow = ind.vwap?.[i];
    const up1Now = ind.up1?.[i];
    const dn1Now = ind.dn1?.[i];
    const close = bar.close;
    const side = armedState.side;
    const tickSize = params.tickSize || 0.25;
    const stopPrice = side === 'long'
      ? armedState.extPrice - params.stopTicks * tickSize
      : armedState.extPrice + params.stopTicks * tickSize;

    const stopDist = Math.abs(close - stopPrice);
    if (!(stopDist > 0) || !Number.isFinite(vwapNow)) return [];
    if (Math.abs(vwapNow - close) < (params.minRewardR || 0) * stopDist) return [];

    state.setups[setupKey] = { triggered: true };

    const tp2Price = side === 'long' ? up1Now : dn1Now;
    return [{
      type: 'enter',
      orderType: 'market',
      side,
      stopPrice,
      takeProfits: [
        { id: 'tp1', price: vwapNow, qtyPct: params.tp1Pct ?? 0.5 },
        { id: 'tp2', price: tp2Price, qtyPct: 1 - (params.tp1Pct ?? 0.5) },
      ],
    }];
  },
};

function findSwing(bars, lo, hi, side) {
  let best = null;
  for (let j = lo; j <= hi; j++) {
    const bar = bars[j];
    if (!bar) continue;
    if (side === 'long') {
      if (!best || bar.low < best.price) best = { index: j, price: bar.low };
    } else if (!best || bar.high > best.price) {
      best = { index: j, price: bar.high };
    }
  }
  return best;
}

function cvdExtreme(cvd, idx, side, window = 2) {
  const lo = Math.max(0, idx - window);
  const hi = Math.min(cvd.length - 1, idx + window);
  let best = cvd[idx] ?? 0;
  for (let j = lo; j <= hi; j++) {
    if (!Number.isFinite(cvd[j])) continue;
    if (side === 'long') best = Math.min(best, cvd[j]);
    else best = Math.max(best, cvd[j]);
  }
  return best;
}

function findDivergence(bars, cvd, armedState, params) {
  const { side, extIdx, extPrice } = armedState;
  const lo = Math.max(0, extIdx - params.divLookback);
  const hi = Math.max(lo, extIdx - params.divMinGap);
  if (hi <= lo) return false;
  const p1 = findSwing(bars, lo, hi, side);
  if (!p1) return false;

  const cvdP1 = cvdExtreme(cvd, p1.index, side, 2);
  const cvdP2 = cvdExtreme(cvd, extIdx, side, 2);
  const minDelta = params.divMinDelta || 0;

  if (side === 'long') {
    return extPrice < p1.price && cvdP2 > cvdP1 + minDelta;
  }
  return extPrice > p1.price && cvdP2 < cvdP1 - minDelta;
}

function checkMultiTfConfirm(bars, ind, i, armedState, params) {
  const tfs = parseConfirmTfs(params.confirmTfs);
  const { side, extIdx } = armedState;
  const close1 = bars[i]?.close;
  const up2 = ind.up2?.[i];
  const dn2 = ind.dn2?.[i];
  if (![close1, up2, dn2].every(Number.isFinite)) return false;

  const inside1 = side === 'long' ? close1 > dn2 : close1 < up2;
  if (!inside1 || i <= extIdx) return false;
  if (!tfs.has(5) && !tfs.has(30) && tfs.has(1)) return true;

  if (tfs.has(5)) {
    const c5 = ind.tf5Close?.[i];
    const idx5 = ind.tf5Idx?.[i];
    if (!Number.isFinite(c5) || !Number.isFinite(idx5) || idx5 <= extIdx) return false;
    const inside5 = side === 'long' ? c5 > dn2 : c5 < up2;
    if (!inside5) return false;
  }
  if (tfs.has(30)) {
    const c30 = ind.tf30Close?.[i];
    const idx30 = ind.tf30Idx?.[i];
    if (!Number.isFinite(c30) || !Number.isFinite(idx30) || idx30 <= extIdx) return false;
    const inside30 = side === 'long' ? c30 > dn2 : c30 < up2;
    if (!inside30) return false;
  }
  return true;
}

export default vwapAbsorption;
